import i18n from "@/i18n";
import {Role} from "@/types";
import {useMemberStore} from "@/stores/memberStore";
import {createRouter, createWebHistory} from "vue-router";

import Account from "../views/Account.vue";
import Coproperty from "../views/Coproperty.vue";
import CopropertyIndex from "../views/CopropertyIndex.vue";
import Company from "@/views/Company.vue";
import Invoices from "../views/Invoices.vue";
import Membership from "@/views/Membership.vue";
import Syndicate from "../views/Syndicate.vue";

import Admin from "../views/admin/Admin.vue";
import AdminActivityForm from "../views/admin/AdminActivityForm.vue";
import AdminActivityOccurrenceIndex from "../views/admin/AdminActivityOccurrenceIndex.vue";
import AdminActivityOccurrenceParticipants from "@/views/admin/AdminActivityOccurrenceParticipants.vue";
import AdminActivityOccurrenceParticipantTransfer from "@/views/admin/AdminActivityOccurrenceParticipantTransfer.vue";
import AdminActivityOccurrenceRegistrations from "@/views/admin/AdminActivityOccurrenceRegistrations.vue";
import AdminOrganizationIndex from "@/views/admin/AdminOrganizationIndex.vue";
import AdminOrganizationView from "@/views/admin/AdminOrganizationView.vue";
import AdminOrganizationMergeForm from "@/views/admin/AdminOrganizationMergeForm.vue";

import AdminMembershipsIndex from "../views/admin/AdminMembershipsIndex.vue";

import MemberActivitiesView from "@/views/member/MemberActivitiesView.vue";
import AddMemberForm from "@/views/shared/AddMemberForm.vue";
import AddContactForm from "@/views/shared/AddContactForm.vue";
import SyndicateIndex from "@/views/SyndicateIndex.vue";
import CompanyIndex from "@/views/CompanyIndex.vue";
import EditMemberForm from "@/views/shared/EditMemberForm.vue";
import EditContactForm from "@/views/shared/EditContactForm.vue";
import AdminBookIndex from "@/views/admin/AdminBookIndex.vue";
import AdminAddBookForm from "@/views/admin/AdminAddBookForm.vue";
import AdminEditBookForm from "@/views/admin/AdminEditBookForm.vue";

import DocumentCategory from "@/views/DocumentCategory.vue";
import DocumentForm from "@/views/DocumentForm.vue";

const router = createRouter({
  // eslint-disable-next-line
  scrollBehavior() {
    // always scroll to top
    return {top: 0};
  },
  history: createWebHistory(),
  routes: [
    {
      path: i18n.t("routes.account.path"),
      name: "account",
      component: Account,
    },
    {
      path: i18n.t("routes.coproperty.path"),
      name: "coproperty",
      component: Coproperty,
      children: [
        {
          path: "",
          name: "coproperty.index",
          component: CopropertyIndex,
        },
        {
          path: i18n.t("routes.coproperty.children.documentCategory.path"),
          name: "coproperty.children.documentCategory",
          component: DocumentCategory,
          props: true
        },
        {
          path: i18n.t("routes.coproperty.children.addDocument.path"),
          name: "coproperty.children.addDocument",
          component: DocumentForm,
        },
        {
          path: i18n.t("routes.coproperty.children.editDocument.path"),
          name: "coproperty.children.editDocument",
          component: DocumentForm,
        }
      ],
    },
    {
      path: i18n.t("routes.syndicate.path"),
      name: "syndicate",
      component: Syndicate,
      meta: {
        requiredRole: [Role.RegularSyndicateAdmin, Role.RegularSyndicateManager, Role.RegularSyndicateCoOwner],
      },
      children: [
        {
          path: "",
          name: "syndicate.index",
          component: SyndicateIndex,
        },
        {
          path: i18n.t("routes.syndicate.children.addMember.path"),
          alias: i18n.t("routes.syndicate.children.addMember.path"),
          name: "syndicate.children.addMember",
          component: AddMemberForm,
          meta: {
            requiredRole: [Role.RegularSyndicateAdmin, Role.RegularSyndicateManager],
          },
          props: () => {
            const memberStore = useMemberStore();
            return ({organizationId: memberStore.getMember.organizationId})
          }
        },
        {
          path: i18n.t("routes.syndicate.children.editMember.path"),
          alias: i18n.t("routes.syndicate.children.editMember.path").replace(':memberId', ''),
          name: "syndicate.children.editMember",
          component: EditMemberForm,
          meta: {
            requiredRole: [Role.RegularSyndicateAdmin, Role.RegularSyndicateManager],
          },
          props: true
        }
      ],
    },
    {
      path: i18n.t("routes.company.path"),
      name: "company",
      component: Company,
      meta: {
        requiredRole: [Role.CorporateAdmin, Role.RealEstateBroker],
      },
      children: [
        {
          path: "",
          name: "company.index",
          component: CompanyIndex,
        },
        {
          path: i18n.t("routes.company.children.addContact.path"),
          alias: i18n.t("routes.company.children.addContact.path"),
          name: "company.children.addContact",
          component: AddContactForm,
          props: () => {
            const memberStore = useMemberStore();
            return ({organizationId: memberStore.getMember.organizationId})
          }
        },
        {
          path: i18n.t("routes.company.children.editContact.path"),
          alias: i18n.t("routes.company.children.editContact.path").replace(':contactId', ''),
          name: "company.children.editContact",
          component: EditContactForm,
          props: true
        }
      ],
    },
    {
      path: i18n.t("routes.membership.path"),
      name: "membership",
      component: Membership,
      meta: {
        requiredRole: Role.RegularIndividual,
      }
    },
    {
      path: i18n.t("routes.invoices.path"),
      name: "invoices",
      component: Invoices,
    },
    {
      path: i18n.t("routes.activities.path"),
      name: "activities",
      component: MemberActivitiesView,
    },
    {
      path: i18n.t("routes.admin.path"),
      name: "admin",
      component: Admin,
      meta: {
        requiredRole: Role.Admin,
        noLinkInBreadcrumbs: true,
      },
      children: [
        {
          path: i18n.t("routes.admin.children.books.path"),
          name: "admin.children.books",
          component: Admin,
          children: [
            {
              path: "",
              name: "admin.children.books.index",
              component: AdminBookIndex,
            },
            {
              path: i18n.t("routes.admin.children.books.add.path"),
              name: "admin.children.books.add",
              component: AdminAddBookForm,
            },
            {
              path: i18n.t("routes.admin.children.books.edit.path"),
              alias: i18n.t("routes.admin.children.books.edit.path").replace(':id', ''),
              name: "admin.children.books.edit",
              component: AdminEditBookForm,
              props: true
            },
          ],
        },
        {
          path: i18n.t("routes.admin.children.organizations.path"),
          name: "admin.children.organizations",
          component: Admin,
          children: [
            {
              path: "",
              name: "admin.children.organizations.index",
              component: AdminOrganizationIndex,
            },
            {
              path: i18n.t("routes.admin.children.organizations.merge.path"),
              alias: i18n.t("routes.admin.children.organizations.merge.path"),
              name: "admin.children.organizations.merge",
              component: AdminOrganizationMergeForm
            },
            {
              path: i18n.t("routes.admin.children.organizations.view.path"),
              alias: i18n.t("routes.admin.children.organizations.view.path")
                .replace(':organizationId', ''),
              name: "admin.children.organizations.view",
              component: AdminOrganizationView,
              props: true
            },
            {
              path: i18n.t("routes.admin.children.organizations.addMember.path"),
              alias: i18n.t("routes.admin.children.organizations.addMember.path")
                .replace(':organizationId', ''),
              name: "admin.children.organizations.addMember",
              component: AddMemberForm,
              props: true
            },
            {
              path: i18n.t("routes.admin.children.organizations.editMember.path"),
              alias: i18n.t("routes.admin.children.organizations.editMember.path")
                .replace(':organizationId', '')
                .replace(':memberId', ''),
              name: "admin.children.organizations.editMember",
              component: EditMemberForm,
              props: true
            },
            {
              path: i18n.t("routes.admin.children.organizations.addContact.path"),
              alias: i18n.t("routes.admin.children.organizations.addContact.path")
                .replace(':organizationId', ''),
              name: "admin.children.organizations.addContact",
              component: AddContactForm,
              props: true
            },
            {
              path: i18n.t("routes.admin.children.organizations.editContact.path"),
              alias: i18n.t("routes.admin.children.organizations.editContact.path")
                .replace(':organizationId', '')
                .replace(':contactId', ''),
              name: "admin.children.organizations.editContact",
              component: EditContactForm,
              props: true
            }
          ],
        },
        {
          path: i18n.t("routes.admin.children.memberships.path"),
          name: "admin.children.memberships",
          component: AdminMembershipsIndex,
        },
        {
          path: i18n.t("routes.admin.children.activities.path"),
          name: "admin.children.activities",
          component: Admin,
          children: [
            {
              path: "",
              name: "admin.children.activities.index",
              component: AdminActivityOccurrenceIndex,
            },
            {
              path: i18n.t("routes.admin.children.activities.add.path"),
              name: "admin.children.activities.add",
              component: AdminActivityForm
            },
            {
              path: i18n.t("routes.admin.children.activities.update.path"),
              alias: i18n.t("routes.admin.children.activities.update.edit.path")
                .replace(':id', ''),
              name: "admin.children.activities.update",
              component: AdminActivityForm,
              props: true
            },
            {
              path: i18n.t("routes.admin.children.activities.participants.edit.path"),
              alias: i18n.t("routes.admin.children.activities.participants.edit.path")
                .replace(':activityId', '')
                .replace(':activityOccurrenceId', ''),
              name: "admin.children.activities.participants.edit",
              component: AdminActivityOccurrenceParticipants,
              props: true
            },
            {
              path: i18n.t("routes.admin.children.activities.participants.add.path"),
              alias: i18n.t("routes.admin.children.activities.participants.add.path"),
              name: "admin.children.activities.participants.add",
              component: AdminActivityOccurrenceParticipants
            },
            {
              path: i18n.t("routes.admin.children.activities.registrations.edit.path"),
              alias: i18n.t("routes.admin.children.activities.registrations.edit.path").replace(':activityId', ''),
              name: "admin.children.activities.registrations.edit",
              component: AdminActivityOccurrenceRegistrations,
              props: true
            },
            {
              path: i18n.t("routes.admin.children.activities.registrations.add.path"),
              alias: i18n.t("routes.admin.children.activities.registrations.add.path"),
              name: "admin.children.activities.registrations.add",
              component: AdminActivityOccurrenceRegistrations
            },
            {
              path: i18n.t("routes.admin.children.activities.participantTransfer.path"),
              alias: i18n.t("routes.admin.children.activities.participantTransfer.path").replace(':activityOccurrenceId', '')
                .replace(':participantId', ''),
              name: "admin.children.activities.participantTransfer",
              component: AdminActivityOccurrenceParticipantTransfer,
              props: true
            },
          ],
        }
      ],
    }
  ],
});

// eslint-disable-next-line no-unused-vars
router.beforeEach(async (to) => {
  const memberStore = useMemberStore()
  if (!to.meta.requiredRole)
    return;
  const isRoleArray = Array.isArray(to.meta.requiredRole)
  const doesNotHaveGivenRole = !isRoleArray && !memberStore.hasRole(to.meta.requiredRole as Role);
  const hasNoRoleAmountRoleList = isRoleArray && !memberStore.hasOneOfTheseRoles(to.meta.requiredRole as Role[]);
  if (doesNotHaveGivenRole || hasNoRoleAmountRoleList) {
    return {
      name: "account",
    };
  }
});

export const Router = router;